// Lightweight context that exposes the user's local sample library
// to whichever rail components want to display it. The provider owns
// (a) the in-memory mirror of `storage.listSamples()` and (b) a
// refresh trigger that callers (the Forge commit flow, deletes,
// renames, …) bump after they mutate IDB.
//
// Why a context rather than a hook directly: there's exactly one
// SampleStorage in the page (a process-wide singleton from
// `getSampleStorage()`), and several distant components — MINE list,
// the Forge mode pane, eventually the COMMUNITY insert path — all
// want the same view of it. A single provider keeps them in sync
// without prop drilling or duplicate IDB reads.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { SampleStorage } from '../../samples/storage/idb';
import type { Sample } from '../../samples/types';

type SampleStoreValue = {
  /** Snapshot of `storage.listSamples()` at the last refresh. */
  samples: Sample[];
  /** True until the first listSamples() resolves. */
  loading: boolean;
  /** Re-read from storage. Bumps a counter that fires the effect. */
  refresh: () => void;
  /** The underlying storage handle. May be null if the engine hasn't
   *  finished booting (rare — most callers can trust it). */
  storage: SampleStorage | null;
};

const SampleStoreContext = createContext<SampleStoreValue | null>(null);

export type SampleStoreProviderProps = {
  storage: SampleStorage | null;
  children: React.ReactNode;
};

export function SampleStoreProvider({ storage, children }: SampleStoreProviderProps) {
  const [samples, setSamples] = useState<Sample[]>([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!storage) {
      setSamples([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    storage
      .listSamples()
      .then((rows) => {
        if (cancelled) return;
        // Newest first — matches publish-time intuition for a "your
        // samples" list. The IDB index is unsorted; sort here.
        const sorted = [...rows].sort((a, b) => b.createdAt - a.createdAt);
        setSamples(sorted);
      })
      .catch(() => {
        if (cancelled) return;
        setSamples([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [storage, tick]);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  const value = useMemo(
    () => ({ samples, loading, refresh, storage }),
    [samples, loading, refresh, storage],
  );

  return (
    <SampleStoreContext.Provider value={value}>
      {children}
    </SampleStoreContext.Provider>
  );
}

export function useSampleStore(): SampleStoreValue {
  const ctx = useContext(SampleStoreContext);
  if (!ctx) {
    throw new Error('useSampleStore must be called inside <SampleStoreProvider>');
  }
  return ctx;
}
